import React, { useState } from 'react';
import { useParams } from 'react-router';

import styled from '@emotion/styled';

import Stars from '../../../components/Badge/Stars';
import StatusCell from '../../../components/Badge/StatusCell';
import { FormSubmitButton } from '../../../components/Button/FormSubmitButton';
import KycRequired from '../../../components/Cards/KycRequired';
import { Modal } from '../../../components/Modal/Modal';
import HireDispatcherModal from '../../../components/Modal/ModalActions/HireDispatcherModal/HireDispatcherModal';
import KycInformationModal from '../../../components/Modal/ModalActions/KycInformationModal/KycInformationModal';
import RehireDriverLookupFlowModal from '../../../components/Modal/ModalActions/RehireDriverLookupFlowModal/RehireDriverLookupFlowModal';
import Tooltip from '../../../components/Tooltip/Tooltip';
import { useAppSelector } from '../../../redux/store';

const DriverLookupBioArea = () => {
  const { kycStatusData } = useAppSelector((state) => state.kycStatusData);
  const { authData } = useAppSelector((state) => state.auth);
  const [showHireModal, setShowHireModal] = useState(false);
  const [showRehireModal, setShowRehireModal] = useState(false);
  const { businessInfo } = useAppSelector((state) => state.businessInfoData);
  const [showKycRequiredModal, setShowKycRequiredModal] = useState(false);
  const [showKycSetupModal, setShowKycSetupModal] = useState(false);

  const { jsonData } = useParams();

  const objectFyData = jsonData
    ? JSON.parse(decodeURIComponent(jsonData ?? ''))
    : null;

  const successActionFn = () => {
    setShowKycRequiredModal(false);
    setShowKycSetupModal(true);
  };

  const onClickHireDriverBtnFn = () => {
    !['Accepted'].includes(kycStatusData?.kycStatus ?? '')
      ? setShowKycRequiredModal(true)
      : setShowHireModal(true);
  };

  const onClickRehireDriveBtnFn = () => {
    !['Accepted'].includes(kycStatusData?.kycStatus ?? '')
      ? setShowKycRequiredModal(true)
      : setShowRehireModal(true);
  };

  return (
    <Container>
      <MainSide>
        <Img
          src={
            !!objectFyData?.profilePicture
              ? objectFyData.profilePicture
              : '/static/img/profilePlaceholder2.png'
          }
        />
        <TextArea>
          <FirstTextRow>
            <BoldText>
              {objectFyData?.firstName} {objectFyData?.lastName}
            </BoldText>
          </FirstTextRow>

          <TextRow>Driver</TextRow>

          <TextRow>
            <div>Average Timeline:</div>
            <BoldNumberText>
              {objectFyData?.timeAtCompany || 'N/A'}
            </BoldNumberText>
          </TextRow>

          <TextRow className={'broken'}>
            <StarArea>
              <Stars numStars={Number(objectFyData?.averageRating)} />{' '}
              <Tooltip content='This shows the average cumulative star rating of a driver across all trips completed for all companies' />
              <BoldNumberText className='margin-left'>
                {Number(objectFyData?.averageRating ?? 0)} (
                {objectFyData?.totalNumberOfRatings || 0})
              </BoldNumberText>
            </StarArea>
            <div>Current company:</div>
            <BoldNumberText>
              {objectFyData?.currentCompanyName || 'N/A'}
            </BoldNumberText>

            {objectFyData?.currentCompanyName ? (
              <StatusCell
                data={{ status: objectFyData?.employmentType || 'N/A' }}
                statusKey='status'
                customClasses='small-border-radius'
              />
            ) : (
              <StatusCell
                data={{ status: 'unemployed' }}
                statusKey='status'
                customClasses='small-border-radius'
              />
            )}
          </TextRow>
        </TextArea>
      </MainSide>

      {businessInfo?.status === 'Active' &&
      objectFyData?.currentCompanyId !== authData?.user?.companyId ? (
        objectFyData?.canRehire ? (
          <FormSubmitButton
            className='clear blue-text heavy-text auto-left-margin full-mobile'
            onClick={onClickRehireDriveBtnFn}
          >
            Rehire Driver
          </FormSubmitButton>
        ) : (
          <FormSubmitButton
            className='clear blue-text heavy-text auto-left-margin full-mobile'
            onClick={onClickHireDriverBtnFn}
          >
            Hire Driver
          </FormSubmitButton>
        )
      ) : null}

      <Modal showModal={showHireModal} setShowModal={setShowHireModal}>
        <HireDispatcherModal dispatcherData={objectFyData} />
      </Modal>

      <Modal showModal={showRehireModal} setShowModal={setShowRehireModal}>
        <RehireDriverLookupFlowModal dispatcherData={objectFyData} />
      </Modal>

      <Modal
        showModal={showKycRequiredModal}
        setShowModal={setShowKycRequiredModal}
      >
        <KycRequired
          successActionFn={successActionFn}
          message='You need to complete your KYC to hire a driver'
        />
      </Modal>

      <Modal showModal={showKycSetupModal} setShowModal={setShowKycSetupModal}>
        <KycInformationModal />
      </Modal>
    </Container>
  );
};

export default DriverLookupBioArea;

const StarArea = styled.div`
  display: flex;
  /* gap: 8px; */
  align-items: center;
  margin-right: 8px;
`;

const MainSide = styled.div`
  display: flex;
  gap: 16px;
`;

const Container = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  padding: 32px;
  display: flex;
  gap: 16px;
  max-width: 827px;
  border-radius: 5px;

  gap: 24px;

  @media (max-width: 884px) {
    flex-direction: column;
    gap: 16px;
    padding: 20px;
  }
`;

const TextArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Img = styled.img`
  width: 91px;
  height: 91px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid transparent;

  @media (max-width: 884px) {
    min-width: 91px;
    min-height: 91px;
  }
`;

const BoldText = styled.span`
  font-weight: 600;
  font-size: 18px;
  line-height: 18px;
`;

const FirstTextRow = styled.div`
  display: flex;
  gap: 12px;
`;

const TextRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${({ theme }) => theme.palette.grayTextColor};

  &.broken {
    @media (max-width: 884px) {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
    }
  }
`;

const BoldNumberText = styled.span`
  font-weight: 600;
  font-size: 14px;
  line-height: 14px;

  &.margin-left {
    margin-left: 10px;
  }
`;
